import { TContent, TContentDetailsPayload, TContentType } from '@/types/Content.types';
import { TArticleSectionDraft, TBrandedFormValues, TPendingImageUpload } from './types';

// `details` is typed as an opaque object in the generated schema; the per-type shape
// is documented in the backend content DTO. We narrow with a local cast.
type RawSubsection = { _id?: string; title?: string; text?: string; imageUrl?: string | null; order?: number };
type RawSection = RawSubsection & { subsections?: RawSubsection[] };
type RawDetails = {
  body?: string;
  sections?: RawSection[];
  source?: string;
  fullDescription?: string;
  mux?: { status?: string | null; playbackId?: string | null };
  youtube?: { videoId?: string };
};

export const buildContentDetails = (
  type: TContentType,
  values: TBrandedFormValues,
): TContentDetailsPayload => {
  if (type === 'post') {
    // A post is a single block of text, authored as the description on the details
    // step so it can be seen in the thumbnail preview. The backend still requires a
    // body for posts, so send the same text.
    return { body: values.description.trim() };
  }

  if (type === 'video') {
    // Source-specific details (mux/youtube) are set via their dedicated endpoints;
    // the create/update payload only carries the full description.
    return { fullDescription: values.fullDescription || undefined };
  }

  if (type === 'article') {
    return {
      sections: values.sections.map((section, sectionIndex) => ({
        _id: section._id,
        title: section.title || undefined,
        text: section.text,
        imageUrl: section.imageUrl ?? undefined,
        order: sectionIndex,
        subsections: section.subsections.map((sub, subIndex) => ({
          _id: sub._id,
          title: sub.title || undefined,
          text: sub.text,
          imageUrl: sub.imageUrl ?? undefined,
          order: subIndex,
        })),
      })),
    };
  }

  return {};
};

// All sections + subsections with a newly-picked file to upload after save.
export const collectImageUploads = (values: TBrandedFormValues): TPendingImageUpload[] => {
  const uploads: TPendingImageUpload[] = [];
  for (const section of values.sections) {
    if (section.imageFile) {
      uploads.push({ sectionId: section._id, file: section.imageFile });
    }
    for (const sub of section.subsections) {
      if (sub.imageFile) {
        uploads.push({ sectionId: section._id, subsectionId: sub._id, file: sub.imageFile });
      }
    }
  }
  return uploads;
};

export const mapContentToFormValues = (content: TContent): TBrandedFormValues => {
  const details = (content.details ?? {}) as RawDetails;

  const sections: TArticleSectionDraft[] = (details.sections ?? []).map((section) => ({
    _id: section._id ?? crypto.randomUUID(),
    title: section.title ?? '',
    text: section.text ?? '',
    imageUrl: section.imageUrl ?? null,
    imageFile: null,
    subsections: (section.subsections ?? []).map((sub) => ({
      _id: sub._id ?? crypto.randomUUID(),
      title: sub.title ?? '',
      text: sub.text ?? '',
      imageUrl: sub.imageUrl ?? null,
      imageFile: null,
    })),
  }));

  return {
    title: content.title ?? '',
    description: content.description ?? '',
    domains: content.domains ?? [],
    subdomains: content.subdomains ?? [],
    body: details.body ?? '',
    sections,
    videoSource: details.source === 'youtube' ? 'youtube' : 'mux',
    videoFile: null,
    muxStatus: details.mux?.status ?? null,
    muxPlaybackId: details.mux?.playbackId ?? null,
    youtubeUrl: details.youtube?.videoId ? `https://youtu.be/${details.youtube.videoId}` : '',
    fullDescription: details.fullDescription ?? '',
  };
};
